import * as React from 'react';
import { FormattedMessage } from 'react-intl';

import debounce from 'lodash/debounce';
import PropTypes from 'prop-types';
import type { LegacyContextType } from 'types/legacy-context-types';

import { Divider, Typography, Typography2 } from '@coursera/cds-core';
import { Box, Container, Notification, color, containerMaxWidth, css } from '@coursera/coursera-ui';
import { SvgEdit, SvgTrash } from '@coursera/coursera-ui/svg';

import type { TspCourseRecommendationsQueryLight_EnterpriseProgramsV1Resource_get_tspSummaries_elements as ProgramSkillSet } from 'bundles/enterprise-admin-program-catalog/components/__generated__/TspCourseRecommendationsQueryLight';
import SelectionControl from 'bundles/enterprise-admin-skills/components/SelectionControl';
import { LearnerSkillSetDetails } from 'bundles/enterprise-admin-skills/components/SkillSetDetails/LearnerSkillSetDetails';
import SkillSetEditor from 'bundles/enterprise-admin-skills/components/SkillSetEditor';
import { SkillSetsSelectionControls } from 'bundles/enterprise-admin-skills/components/SkillSetsControls';
import VirtualizedMasonry from 'bundles/enterprise-admin-skills/components/VirtualizedMasonry';
import type { OrganizationSkillSetsQuery_EnterpriseTargetSkillProfileSummariesV1Resource_byOrganization_elements as OrgSkillSet } from 'bundles/enterprise-admin-skills/components/__generated__/OrganizationSkillSetsQuery';
import { org_coursera_skill_GoalType as GoalType } from 'bundles/enterprise-admin-skills/components/__generated__/globalTypes';
import AboutTargetLevelsInformationCard from 'bundles/enterprise-admin-skills/components/staticInformationCards/AboutTargetLevelsInformationCard';
import type { Customization, FunctionMapping } from 'bundles/enterprise-admin-skills/components/types';
import { useEnterpriseAdminContext } from 'bundles/enterprise-admin/EnterpriseAdminContext';
import InformationCardToggle from 'bundles/enterprise-legacy-learner-home/components/InformationCard/InformationCardToggle';
import { AboutSkillSetTypes } from 'bundles/enterprise-skillset-bank/components/AboutSkillSetTypes';
import {
  DuplicateNameNotification,
  SkillSetRenamedSuccessfullyNotification,
  UpdatedSkillSetDescriptionSuccessfullyNotification,
} from 'bundles/enterprise-skillset-bank/components/Notifications';
import OrgLevelSkillSetToggle from 'bundles/enterprise-skillset-bank/components/OrgLevelSkillSetToggle';
import SkillSelectionFilters from 'bundles/enterprise-skillset-bank/components/SkillSelectionFilters';
import SkillSetCard from 'bundles/enterprise-skillset-bank/components/SkillSetCard';
import SkillSetConflictResolution from 'bundles/enterprise-skillset-bank/components/SkillSetConflictResolution';
import SkillSetFinder from 'bundles/enterprise-skillset-bank/components/SkillSetFinder/SkillSetFinder';
import SkillSetReview from 'bundles/enterprise-skillset-bank/components/SkillSetReview';
import styles from 'bundles/enterprise-skillset-bank/components/styles';
import { withSkillsData } from 'bundles/enterprise-skillset-bank/hocs/withSkillsData';
import type { PropsFromWithSkillsData } from 'bundles/enterprise-skillset-bank/hocs/withSkillsData';
import {
  fallbackFunctionId,
  getSkillSetAcademyIds,
  matchDataTechAcademies,
} from 'bundles/enterprise-skillset-bank/utils/academies';
import { Button, Clickable, Spacer } from 'bundles/enterprise-ui/components';
import type { ModalContextType } from 'bundles/enterprise-ui/components';
import type { SkillSet } from 'bundles/naptimejs/resources/skillSets.v1';
import TrackedDiv from 'bundles/page/components/TrackedDiv';

import _t from 'i18n!nls/enterprise-admin-skills';

const Empty = () => (
  <Box
    flexDirection="column"
    alignItems="center"
    htmlAttributes={{
      'data-e2e': '0-skillsets-matches',
    }}
  >
    <Spacer size={32} />
    <Typography2 component="strong">{_t('0 SkillSets match your filter criteria')}</Typography2>
    <Spacer size={12} />
    <Typography2 component="p">{_t('Please change your filter selections')}</Typography2>
    <Spacer size={32} />
  </Box>
);

enum Step {
  SELECTION,
  CONFLICT_RESOLUTION,
  REVIEW,
}

export type PropsFromCaller = {
  onCancel: () => void;
  onNewSkillSetsConfirmed: (ids: string[]) => Promise<void>;
  organizationSkillSets: OrgSkillSet[];
  customizations: Customization[];
  onSkillSetsCustomized: (customizations: Customization[]) => void;
  isFinderInitiallyOpen?: boolean;
  functionMappings?: FunctionMapping[];
  programSkillSets?: ProgramSkillSet[];
  isProgramScope?: boolean;
};

type PropsFromContext = {
  enableDataTechSku?: boolean;
};

type Props = PropsFromCaller & PropsFromWithSkillsData & PropsFromContext;

type Filters = {
  targetSkillProfileIds?: string[];
  lure: string[];
  academyIds: string[];
  occupationIds: string[];
  functionIds: string[];
  industryIds?: string[];
  targetSkillProfileTypes?: string[];
  skillIds?: string[];
  keyword?: string;
};

const defaultFilters: Filters = {
  lure: [GoalType.LITERACY, GoalType.UPSKILL, GoalType.RESKILL],
  academyIds: [],
  functionIds: [],
  occupationIds: [],
};

type State = {
  isInformationCardOpen: boolean;
  step: Step;
  selectedSkillSets: SkillSet[];
  searchText?: string;
  isShowingNoSelectedSkillSetsError?: boolean;
  filterScopeDuplicates: boolean;
  filters: Filters;
  filterOrgLevelOnly: boolean;
  masonryKey?: number;
  skillSetNotifications: { [id: string]: React.ReactNode };
};

const defaultState: State = {
  isInformationCardOpen: false,
  step: Step.SELECTION,
  selectedSkillSets: [],
  filterScopeDuplicates: true,
  filters: defaultFilters,
  filterOrgLevelOnly: false,
  skillSetNotifications: {},
};

const DEBOUNCE_TIME = 500;

export class SkillSetSelection extends React.Component<Props, State> {
  declare masonryRef?: VirtualizedMasonry | null;

  static contextTypes = {
    openModal: PropTypes.func,
    closeModal: PropTypes.func,
  };

  declare context: LegacyContextType<typeof SkillSetSelection.contextTypes>;

  state: State = defaultState;

  declare debouncedOnSearchChange: (keyword: string) => void;

  constructor(props: Props) {
    super(props);
    this.debouncedOnSearchChange = debounce(this.onSearchChange, DEBOUNCE_TIME);
  }

  componentDidMount() {
    setTimeout(() => {
      this.setState({ masonryKey: Date.now() });
    }, 0);
  }

  handleViewSkillSetDetails = (skillSet: SkillSet) => {
    const { openModal } = this.context as ModalContextType;
    openModal({
      children: <LearnerSkillSetDetails skillSet={skillSet} />,
      style: {
        content: {
          top: 'calc(66px + 24px)', // 66px is the height of the global navigation, 24 is just some space to separate the modal from the nav
          transform: 'translateX(-50%)',
          maxWidth: '936px',
          maxHeight: 'calc(100vh - 66px * 2 - 24px * 2)', // 66px is the height of the global navigation, 24 is just some space to separate the modal from the nav
          padding: '0',
        },
      },
    });
  };

  handleSelectAll = () => {
    const filteredSkillSets = this.filterSkillSets();
    if (!filteredSkillSets) {
      return;
    }
    this.setState((state) => ({
      selectedSkillSets: state.selectedSkillSets
        .filter((skillSet) => !filteredSkillSets.some((_) => _.id === skillSet.id))
        .concat(filteredSkillSets),
    }));
  };

  handleDeselectAll = () => {
    const filteredSkillSets = this.filterSkillSets();

    if (!filteredSkillSets) {
      return;
    }
    this.setState((state) => ({
      selectedSkillSets: state.selectedSkillSets.filter(
        (skillSet) => !filteredSkillSets.some((_) => _.id === skillSet.id)
      ),
    }));
  };

  renderSkillSetControls = () => {
    const filteredSkillSets = this.filterSkillSets();
    const { selectedSkillSets } = this.state;

    return (
      <SkillSetsSelectionControls
        selectedSkillSetsCount={selectedSkillSets.length}
        skillSetsCount={filteredSkillSets?.length}
        areAllSelected={
          filteredSkillSets?.every((skillSet) => selectedSkillSets.some((_) => _.id === skillSet.id)) ?? false
        }
        handleSelectAll={this.handleSelectAll}
        handleDeselectAll={this.handleDeselectAll}
      />
    );
  };

  isSkillSetDuplicated = (skillSet: SkillSet) => {
    const { organizationSkillSets } = this.props;
    return organizationSkillSets.some(
      (organizationSkillSet) => organizationSkillSet.title === skillSet.targetSkillProfileName
    );
  };

  isSkillSetCustomized = (skillSet: { id: string }) => {
    const { customizations } = this.props;
    return customizations.some((customization) => customization.id === skillSet.id);
  };

  handleReview = () => {
    const { selectedSkillSets } = this.state;

    if (selectedSkillSets.length === 0) {
      this.setState({ isShowingNoSelectedSkillSetsError: true });
    } else if (this.getSkillSetsRequiringAttention().length > 0) {
      this.setState({ step: Step.CONFLICT_RESOLUTION });
    } else {
      this.setState({ step: Step.REVIEW, isShowingNoSelectedSkillSetsError: false });
    }
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth',
    });
  };

  handleBackFromSkillSetReview = () => {
    window.scrollTo(0, 0);
    if (this.getSkillSetsRequiringAttention().length > 0) {
      this.setState({ step: Step.CONFLICT_RESOLUTION });
    } else {
      this.setState({ step: Step.SELECTION });
    }
  };

  handleBackFromSkillSetConflictResolution = () => {
    this.setState({ step: Step.SELECTION });
    window.scrollTo(0, 0);
  };

  handleSearchTextUpdate = (keyword: string) => {
    this.setState({ searchText: keyword });
    this.debouncedOnSearchChange(keyword);
    setTimeout(() => {
      this.setState({ masonryKey: Date.now() });
    }, DEBOUNCE_TIME);
  };

  onSearchChange = (keyword: string) => {
    const { filters } = this.state;
    const newFilters = { ...filters };
    if (keyword) {
      newFilters.keyword = keyword;
    } else {
      delete newFilters.keyword;
    }
    this.setState({ filters: newFilters });
  };

  toggleInformationCard = () => {
    this.setState(
      (prevState) => ({
        isInformationCardOpen: !prevState.isInformationCardOpen,
      }),
      () => {
        this.masonryRef?._scroller?.updatePosition();
      }
    );
  };

  handleSkillSetUnselected = (id: string) => {
    this.setState((state) => ({
      selectedSkillSets: state.selectedSkillSets.filter((_) => _.id !== id),
    }));
  };

  handleConfirmation = (): Promise<void> => {
    const { onNewSkillSetsConfirmed } = this.props;
    const { selectedSkillSets } = this.state;
    return onNewSkillSetsConfirmed(selectedSkillSets.map((_) => _.id));
  };

  handleConflictResolutionConfirmation = () => {
    const { selectedSkillSets } = this.state;
    const { customizations } = this.props;
    const editedSkillSets = this.getEditedSkillSets();
    const skillSetsRequiringAttention = this.getSkillSetsRequiringAttention();
    const unresolvedSkillSets = skillSetsRequiringAttention.filter((skillSet) =>
      editedSkillSets.every((_) => _.id !== skillSet.id)
    );

    const consolidatedSelectedSkillSets: SkillSet[] = selectedSkillSets.map((skillSet) => {
      const customization = customizations.find((_) => _.id === skillSet.id);
      if (customization) {
        return {
          ...skillSet,
          targetSkillProfileName: customization?.targetSkillProfileName ?? '',
          description: customization?.description ?? '',
        };
      }
      return skillSet;
    });
    this.setState({ selectedSkillSets: consolidatedSelectedSkillSets });
    if (!unresolvedSkillSets.length) {
      this.setState({ step: Step.REVIEW });
    }
  };

  handleNewNotifications = (skillSetId: string, skillSet: SkillSet, name?: string, description?: string) => {
    const { isProgramScope } = this.props;

    let notification: React.ReactNode;

    if (name !== skillSet.targetSkillProfileName) {
      notification = (
        <SkillSetRenamedSuccessfullyNotification
          oldName={skillSet.targetSkillProfileName}
          newName={name}
          onDismiss={() => this.handleDismiss(skillSet.id)}
        />
      );
    } else if (description !== skillSet.description) {
      notification = (
        <UpdatedSkillSetDescriptionSuccessfullyNotification
          skillSetName={skillSet.targetSkillProfileName}
          onDismiss={() => this.handleDismiss(skillSet.id)}
        />
      );
    } else if (this.isSkillSetDuplicated(skillSet)) {
      notification = (
        <DuplicateNameNotification
          name={skillSet.targetSkillProfileName}
          isProgramScope={isProgramScope}
          onRename={this.handleRename(skillSet)}
        />
      );
    }

    if (notification) {
      this.setState((state) => ({
        skillSetNotifications: {
          ...state.skillSetNotifications,
          [skillSetId]: notification,
        },
      }));
    }
  };

  handleRename = (skillSet: SkillSet) => () => {
    const { organizationSkillSets, customizations, onSkillSetsCustomized } = this.props;
    const { openModal, closeModal } = this.context as ModalContextType;

    openModal({
      children: (
        <SkillSetEditor
          name={skillSet?.targetSkillProfileName}
          description={skillSet?.description}
          validateName={(name: string) => organizationSkillSets.every((_) => _.title !== name)}
          onCancel={closeModal}
          onSave={({ name, description }) => {
            onSkillSetsCustomized([
              ...customizations.filter((_) => _.id !== skillSet.id),
              { id: skillSet.id, targetSkillProfileName: name, description },
            ]);
            this.handleNewNotifications(skillSet.id, skillSet, name, description);
            closeModal();
          }}
        />
      ),
    });
  };

  getSkillSetWidth = () => (Math.min(window.innerWidth - 12 * 2, containerMaxWidth.xl) - 24) / 2;

  getEditedSkillSets = () => {
    const { selectedSkillSets } = this.state;
    return selectedSkillSets.filter(this.isSkillSetCustomized);
  };

  getSkillSetsRequiringAttention = () => {
    const { selectedSkillSets } = this.state;
    return selectedSkillSets.filter(this.isSkillSetDuplicated);
  };

  renderSkillSet = (index: number) => {
    const { organizationSkillSets, functionMappings, isProgramScope } = this.props;
    const { selectedSkillSets } = this.state;
    const filteredSkillSets = this.filterSkillSets();
    const skillSet = filteredSkillSets?.[index];
    if (!skillSet) {
      return null;
    }
    const selected = selectedSkillSets.some((_) => _.id === skillSet.id);
    return (
      <SkillSetCard
        skillSet={skillSet}
        onTitleClick={() => this.handleViewSkillSetDetails(skillSet)}
        isAlreadyInTheOrganization={organizationSkillSets.some((_) => _.templateSkillProfileId === skillSet.id)}
        isSelected={selected}
        functionMappings={functionMappings}
        isProgramScope={isProgramScope}
      >
        <SelectionControl
          selected={selected}
          onClick={(event) => {
            event.stopPropagation();
            event.preventDefault();
            this.setState((state) => ({
              selectedSkillSets: selected
                ? state.selectedSkillSets.filter((_) => _.id !== skillSet.id)
                : state.selectedSkillSets.concat(skillSet),
            }));
          }}
          data-e2e={`SkillSetCard-SelectionControl-${skillSet.id}`}
        />
      </SkillSetCard>
    );
  };

  handleDismiss = (skillSetId: string) => {
    this.setState((newState) => ({
      skillSetNotifications: {
        ...newState.skillSetNotifications,
        [skillSetId]: undefined,
      },
    }));
  };

  renderEditableSkillSetCard = (skillSet: SkillSet, index: number) => {
    const { customizations, onSkillSetsCustomized } = this.props;
    const { skillSetNotifications } = this.state;
    const customization = customizations.find(
      (_) =>
        _.id === skillSet.id &&
        (skillSet.targetSkillProfileName !== _.targetSkillProfileName || skillSet.description !== _.description)
    );

    return (
      <div {...css(styles.SkillSetCard)} key={skillSet.id}>
        <SkillSetCard
          skillSet={{ ...skillSet, ...customization }}
          notification={skillSetNotifications[skillSet.id]}
          onTitleClick={() => this.handleViewSkillSetDetails(skillSet)}
        >
          <Box justifyContent="end">
            <Clickable
              onClick={this.handleRename({ ...skillSet, ...customization })}
              data-e2e={`SkillSetCard-edit-${index}`}
            >
              <Box alignItems="center">
                <SvgEdit color={color.primary} size={20} />
                <Spacer size={8} />
                {_t('Edit')}
              </Box>
            </Clickable>
            <Spacer size={24} />
            <Clickable
              onClick={() => {
                this.handleSkillSetUnselected(skillSet.id);
                onSkillSetsCustomized(customizations.filter((_) => _.id !== skillSet.id));
              }}
            >
              <Box alignItems="center">
                <SvgTrash color={color.primary} size={20} />
                <Spacer size={8} />
                {_t('Remove')}
              </Box>
            </Clickable>
          </Box>
        </SkillSetCard>
      </div>
    );
  };

  filterSkillSets = () => {
    const { allSkillSets, organizationSkillSets, programSkillSets, isProgramScope, enableDataTechSku } = this.props;
    const { filterScopeDuplicates, filters, filterOrgLevelOnly } = this.state;
    return allSkillSets
      ?.filter((skillSet) => {
        const skillSetInOrg = organizationSkillSets.some((_) => _.templateSkillProfileId === skillSet.id);
        const skillSetInProgram = programSkillSets?.some((program) => program.templateSkillProfileId === skillSet.id);
        const academyIds = skillSet.academyIds ?? [];

        if (
          getSkillSetAcademyIds(enableDataTechSku).every((academy) => !academyIds.includes(academy)) &&
          academyIds.length > 0
        ) {
          return false;
        }

        if (filterOrgLevelOnly && !skillSetInOrg) {
          return false;
        }
        if (filterScopeDuplicates && (isProgramScope ? skillSetInProgram : skillSetInOrg)) {
          return false;
        }
        if (!filters.lure.includes(skillSet.lure)) {
          return false;
        }
        if (filters.keyword && !skillSet.targetSkillProfileName.toLowerCase().includes(filters.keyword.toLowerCase())) {
          return false;
        }
        if (filters.academyIds.length) {
          const matchesAcademy = filters.academyIds.some((_) => academyIds.includes(_));

          if (!matchesAcademy) {
            return false;
          }
          const matchesFunction = filters.functionIds.includes(skillSet.functionId ?? fallbackFunctionId);
          const matchesOccupation = filters.occupationIds.includes(skillSet.occupationId);

          if (!matchesFunction && !matchesOccupation) {
            return false;
          }
        } else if (enableDataTechSku) {
          const matchesAcademy = matchDataTechAcademies(academyIds);

          if (!matchesAcademy) {
            return false;
          }
        }

        return true;
      })
      ?.sort((a, b) => {
        const aName = a.targetSkillProfileName.toLowerCase();
        const bName = b.targetSkillProfileName.toLowerCase();
        if (aName < bName) return -1;
        if (aName > bName) return 1;
        return 0;
      });
  };

  handleFilterScopeDuplicatesChange = (value: boolean) => {
    this.setState({
      filterScopeDuplicates: value,
      masonryKey: Date.now(),
    });
  };

  handleLureChange = (value: string[]) => {
    this.setState((state) => ({
      ...state,
      filters: { ...state.filters, lure: value },
      masonryKey: Date.now(),
    }));
  };

  handleSkillSetFinderChange = (newFilters: Partial<Filters>) => {
    this.handleSearchTextUpdate('');
    this.setState((state) => ({ ...state, filters: { ...defaultFilters, ...newFilters } }));
  };

  handleAboutSkillSetTypes = () => {
    const { openModal, closeModal } = this.context as ModalContextType;
    openModal({
      children: <AboutSkillSetTypes onClose={closeModal} />,
    });
  };

  handleToggleOrgLevelSkillSets = (orgLevel: boolean) => {
    this.setState({
      filterOrgLevelOnly: orgLevel,
    });
  };

  render() {
    const {
      onCancel,
      organizationSkillSets,
      customizations,
      allSkillSets,
      isFinderInitiallyOpen,
      functionMappings,
      isProgramScope,
    } = this.props;
    if (!allSkillSets) {
      return null;
    }

    const {
      selectedSkillSets,
      searchText,
      step,
      isShowingNoSelectedSkillSetsError,
      isInformationCardOpen,
      filterScopeDuplicates,
      filters,
      filterOrgLevelOnly,
      masonryKey,
    } = this.state;
    const filteredSkillSets = this.filterSkillSets();

    if (step === Step.CONFLICT_RESOLUTION) {
      return (
        <SkillSetConflictResolution
          selectedSkillSets={selectedSkillSets}
          skillSetsRequiringAttention={this.getSkillSetsRequiringAttention()}
          customizations={customizations}
          renderSkillSetCard={this.renderEditableSkillSetCard}
          onBack={this.handleBackFromSkillSetConflictResolution}
          onCancel={onCancel}
          onConfirm={this.handleConflictResolutionConfirmation}
        />
      );
    }
    if (step === Step.REVIEW) {
      return (
        <SkillSetReview
          selectedSkillSets={selectedSkillSets}
          renderSkillSetCard={this.renderEditableSkillSetCard}
          onBack={this.handleBackFromSkillSetReview}
          onCancel={onCancel}
          onConfirm={this.handleConfirmation}
        />
      );
    }

    let children;
    if (filteredSkillSets?.length === 0) {
      children = <Empty />;
    } else {
      children = (
        <div>
          <Box justifyContent="end">
            <Typography2 component="p">{_t('Need more information about our target levels and scores?')}</Typography2>
            <Spacer size={12} />
            <InformationCardToggle
              label={_t('About target levels')}
              isOpen={isInformationCardOpen}
              onClick={this.toggleInformationCard}
              trackingName="about_target_levels_information_link"
              cardHtmlId="admin-dashboard-organization-skillset-target-information"
            />
          </Box>
          <Spacer size={12} />
          <AboutTargetLevelsInformationCard
            id="admin-dashboard-organization-skillset-target-information"
            // @ts-expect-error FIXME result of fixing typing on coursera-ui aphrodite exports in PR#45351
            rootClassName={styles.informationCard}
            isOpen={isInformationCardOpen}
            onRequestClose={this.toggleInformationCard}
          />
          {isInformationCardOpen && <Spacer size={24} />}
          {this.renderSkillSetControls()}
          <Spacer size={24} />
          {masonryKey && (
            <VirtualizedMasonry
              key={masonryKey}
              ref={(node: VirtualizedMasonry | null) => {
                this.masonryRef = node;
              }}
              list={filteredSkillSets ?? []}
              renderItem={this.renderSkillSet}
              itemWidth={this.getSkillSetWidth()}
              // The following is a workaround to re-render VirtualizedMasonry
              // because even though VirtualizedMasonry does not depend on it
              // this.renderSkillSet does.
              //
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-expect-error
              selectedSkillSetsCount={selectedSkillSets.length}
            />
          )}
          <Spacer size={24} />
          {this.renderSkillSetControls()}
        </div>
      );
    }

    return (
      <TrackedDiv
        data-e2e="skillsets-selection"
        trackingName="skillset_selection"
        withVisibilityTracking={true}
        requireFullyVisible={false}
        trackClicks={false}
      >
        <Container isFluid rootClassName={styles.HeaderContainer}>
          <Spacer size={32} />
          <div {...css(styles.ContainerContent)}>
            <Typography variant="h1">
              {isProgramScope ? _t('Add SkillSets to your program') : _t('Add SkillSets to your organization')}
            </Typography>
            <Spacer size={24} />
            {isShowingNoSelectedSkillSetsError && (
              <div data-e2e="no-selected-skillsets-error">
                <Notification
                  type="error"
                  message={
                    <Typography2 component="p">
                      {_t('Error: You will need to select at least one SkillSet in order to proceed to the next step.')}
                    </Typography2>
                  }
                />
                <Spacer size={24} />
              </div>
            )}
            <SkillSetFinder
              allSkillSets={allSkillSets}
              filters={filters}
              onChange={this.handleSkillSetFinderChange}
              isFinderOpenInitially={isFinderInitiallyOpen}
              functionMappings={functionMappings}
            />
            {isProgramScope && (
              <OrgLevelSkillSetToggle
                filterOrgLevelOnly={filterOrgLevelOnly}
                toggleAllSkillSets={() => this.handleToggleOrgLevelSkillSets(false)}
                toggleOrgLevelSkillSets={() => this.handleToggleOrgLevelSkillSets(true)}
              />
            )}
            <Divider />
            <Spacer size={24} />
            <SkillSelectionFilters
              searchText={searchText}
              onSearchTextChange={this.handleSearchTextUpdate}
              lure={filters.lure}
              onLureChange={this.handleLureChange}
              hasSkillSetsInScope={Boolean(organizationSkillSets.length)}
              filterScopeDuplicates={filterScopeDuplicates}
              onFilterSkillSetsInScope={this.handleFilterScopeDuplicatesChange}
              isProgramScope={isProgramScope}
              onAboutSkillSetTypes={this.handleAboutSkillSetTypes}
            />
            <Spacer size={32} />
          </div>
        </Container>
        <Container isFluid rootClassName={styles.ListContainer}>
          <Spacer size={32} />
          <div {...css(styles.ContainerContent)}>{children}</div>
          <Spacer size={32} />
        </Container>
        <Container isFluid rootClassName={styles.FooterContainer}>
          <Spacer size={24} />
          <Box justifyContent="between" alignItems="center" rootClassName={styles.ContainerContent}>
            <Typography2 component="strong">
              <FormattedMessage
                message={_t('{number} {number, plural, =1 {SkillSet} other {SkillSets}} selected')}
                number={selectedSkillSets?.length?.toString()}
              />
            </Typography2>
            <Box>
              <Button variant="secondary" onClick={onCancel} data-e2e="cancel-skillsets-selection">
                {_t('Cancel')}
              </Button>
              <Spacer size={12} />
              <Button variant="primary" onClick={this.handleReview} data-e2e="review-selection">
                {_t('Review Selections')}
              </Button>
            </Box>
          </Box>
          <Spacer size={24} />
        </Container>
      </TrackedDiv>
    );
  }
}

export default withSkillsData<PropsFromCaller>((props) => {
  const { enableDataTechSku } = useEnterpriseAdminContext();
  return <SkillSetSelection {...props} enableDataTechSku={enableDataTechSku} />;
});
